import * as React from "react";

import { useUser } from "@/web/auth/useUser";
import { env } from "@/web/lib/env";
import { cn } from "@/web/lib/styling";

function initialsFromEmail(email: string | undefined): string {
  if (!email) return "?";
  const local = email.split("@")[0] ?? email;
  if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  return (local[0] ?? "?").toUpperCase();
}

export type AuthAvatarProps = React.ComponentPropsWithoutRef<"button">;

/**
 * Circular account trigger (initials from Spacetime `currentUser.email`).
 * Callers must wrap in `<Suspense>` — `useUser` suspends until the row is loaded.
 */
export const AuthAvatar = React.forwardRef<HTMLButtonElement, AuthAvatarProps>(function AuthAvatar(
  { className, type = "button", ...rest },
  ref,
) {
  const user = useUser();
  const email = user?.email;
  const label = email ?? "Account";
  const initials = initialsFromEmail(email);
  const avatarUrl = user?.avatarCdnPath
    ? `${env.VITE_WITHCO_IMAGE_CDN_BASE}/${user.avatarCdnPath}`
    : undefined;

  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        // `inline-flex` + `items-center` centers the initials at ANY size. This
        // used to be `leading-10`, a line-height hardcoded to the default
        // `size-10` — so a caller overriding the size (e.g. the TTW hero
        // header's `size-9`) kept the 40px line box inside a smaller circle and
        // the initials sat visibly below center.
        "relative inline-flex size-10 shrink-0 items-center justify-center",
        "overflow-hidden rounded-full border border-border bg-muted",
        "font-withus-sans text-center text-sm font-semibold text-foreground outline-none",
        "transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
      aria-label={label}
      {...rest}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="size-full object-cover" />
      ) : (
        // `leading-none`: the flex centering above positions the initials, so
        // the glyph box must not add leading of its own.
        <span className="leading-none">{initials}</span>
      )}
    </button>
  );
});
